import {
  ChangeDetectionStrategy,
  Component,
  forwardRef,
  input,
  signal
} from '@angular/core';
import { InputNumberModule } from 'primeng/inputnumber';
import {
  ControlValueAccessor,
  FormsModule,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import { AppTranslationPipe } from '@adapter/i18n/app-translation.pipe';

/**
 * Componente de input para formularios de tipo número
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-input-number',
  imports: [FormsModule, InputNumberModule, AppTranslationPipe],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AppInputNumberComponent),
      multi: true
    }
  ],
  template: `
    <div class="input-number-wrapper">
      <label class="input-label-item" for="{{ id() }}">{{
        label() | translate
      }}</label>
      <p-inputnumber
        class="w-full"
        [showButtons]="incrementor() && decrementor()"
        [useGrouping]="useSeparators()"
        buttonLayout="horizontal"
        [inputId]="id()"
        [maxFractionDigits]="2"
        spinnerMode="horizontal"
        [step]="1"
        [disabled]="isDisabled()"
        [(ngModel)]="value"
        (ngModelChange)="updateValue($event)"
      >
        @if (incrementor()) {
          <ng-template #incrementbuttonicon>
            <span class="pi pi-plus"></span>
          </ng-template>
        }
        @if (decrementor()) {
          <ng-template #decrementbuttonicon>
            <span class="pi pi-minus"></span>
          </ng-template>
        }
      </p-inputnumber>
    </div>
  `,
  styles: ``
})
export class AppInputNumberComponent implements ControlValueAccessor {
  /**
   * Identificador del input
   */
  id = input.required<string>();

  /**
   * Placeholder del input
   */
  label = input.required<string>();

  /**
   * Indica si se muestran separadores de miles
   */
  useSeparators = input<boolean>(true);

  /**
   * Indica si se muestra el botón de incremento
   */
  incrementor = input<boolean>(false);

  /**
   * Indica si se muestra el botón de decremento
   */
  decrementor = input<boolean>(false);

  /**
   * Valor del input
   */
  value = signal<number>(0);

  /**
   * Indica si el input está deshabilitado
   */
  isDisabled = signal(false);

  onChange?: (value: number) => void;

  onTouched?: () => void;

  writeValue(value: number): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled.set(isDisabled);
  }

  updateValue(event: number): void {
    this.value.set(event);
    this.onChange!(event);
    this.onTouched!();
  }
}
